import React from 'react'
import { CheckCircleIcon, ExclamationCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline'
import { Card, CardContent } from '../../../components/ui/card'
import { buildEcddNavSections, scrollToEcddSection } from './ecddDetailNavSections'

function NavStatusIcon({ status }) {
  if (status === 'done') {
    return <CheckCircleIcon className="w-4 h-4 text-green-600 shrink-0" aria-hidden />
  }
  if (status === 'required') {
    return <ExclamationCircleIcon className="w-4 h-4 text-red-500 shrink-0" aria-hidden />
  }
  if (status === 'info') {
    return <MinusCircleIcon className="w-4 h-4 text-slate-400 shrink-0" aria-hidden />
  }
  return <MinusCircleIcon className="w-4 h-4 text-slate-300 shrink-0" aria-hidden />
}

function statusLabelForItem(item) {
  if (item.statusLabel) return item.statusLabel
  if (item.status === 'done') return 'Complete'
  if (item.status === 'required') return 'Required'
  if (item.status === 'info') return 'Reference'
  return 'Optional'
}

/**
 * Left-rail section navigator with overall form progress.
 */
export default function EcddDetailNav({
  formAnswers,
  formGateEval,
  formPct,
  formGatesDone,
  formGatesTotal,
  formGatesReady,
  notesCount = 0,
  activeSectionId,
  onNavigate,
}) {
  const sections = buildEcddNavSections({
    formAnswers,
    formGateEval,
    notesCount,
  })

  const handleClick = (sectionId) => {
    scrollToEcddSection(sectionId)
    onNavigate?.(sectionId)
  }

  return (
    <Card className="border border-slate-200 shadow-md overflow-hidden bg-white shrink-0">
      <CardContent className="p-3.5">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Form sections</div>
          <span
            className={`text-sm font-bold tabular-nums ${formGatesReady ? 'text-green-600' : 'text-psg-teal'}`}
          >
            {formPct}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-2">
          <div
            className="h-full rounded-full transition-all duration-300 bg-psg-teal"
            style={{ width: `${formPct}%`, backgroundColor: formGatesReady ? '#16a34a' : undefined }}
          />
        </div>
        <p className="text-[11px] text-slate-500 m-0 mb-3">
          {formGatesDone}/{formGatesTotal} gates complete
          <span className="mx-1">·</span>
          <span className={formGatesReady ? 'text-green-600 font-medium' : 'text-amber-700 font-medium'}>
            {formGatesReady ? 'Ready for decision' : 'Complete required sections'}
          </span>
        </p>

        <nav aria-label="ECDD form sections">
          <ul className="m-0 p-0 list-none space-y-0.5">
            {sections.map((item) => {
              const active = activeSectionId === item.id
              const label = statusLabelForItem(item)
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleClick(item.id)}
                    className={`w-full flex items-start gap-2 rounded-lg px-2 py-2 text-left transition-colors border ${
                      active
                        ? 'border-psg-teal/40 bg-[#0A516908]'
                        : 'border-transparent hover:bg-slate-50'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5 ${
                        active ? 'bg-psg-teal text-white' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {item.number}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13px] font-medium text-slate-800 leading-snug">{item.title}</span>
                      <span
                        className={`text-[10px] font-semibold ${
                          item.status === 'done'
                            ? 'text-green-700'
                            : item.status === 'required'
                              ? 'text-red-600'
                              : 'text-slate-400'
                        }`}
                      >
                        {label}
                      </span>
                    </span>
                    <NavStatusIcon status={item.status} />
                  </button>
                </li>
              )
            })}
          </ul>
        </nav>
      </CardContent>
    </Card>
  )
}
